import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Component } from '../../types';
import { useEditorStore } from '../../stores/editor/editor-store';
import { pmTheme } from '../pm/pmTheme';

export interface ChecklistItem {
  id: string;
  label: string;
  checked: boolean;
  assignee?: string;
  dueDate?: string;
  priority?: 'critical' | 'high' | 'medium' | 'low';
  group?: string;
  notes?: string;
}

export interface ChecklistProperties {
  items: ChecklistItem[];
  title?: string;
  showProgress: boolean;
  allowReorder: boolean;
  groupBy?: string;
  dataSource?: {
    type: 'query-template';
    queryDefinition?: { queryTemplateId: string; variables?: Record<string, unknown> };
  };
  writeback?: {
    queryTemplateId?: string;
  };
}

export interface ChecklistProps {
  component: Component;
  data?: Record<string, unknown>[];
  isViewerMode?: boolean;
}

type ChecklistPriority = NonNullable<ChecklistItem['priority']>;

const CHECKLIST_PRIORITIES = new Set<string>(['critical', 'high', 'medium', 'low']);

const PRIORITY_COLORS: Record<ChecklistPriority, string> = {
  critical: pmTheme.danger,
  high: pmTheme.warning,
  medium: pmTheme.primary,
  low: pmTheme.textMuted,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isChecklistPriority(value: unknown): value is ChecklistPriority {
  return typeof value === 'string' && CHECKLIST_PRIORITIES.has(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function normalizeChecklistItem(value: unknown, index: number): ChecklistItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const label = optionalString(value.label);
  if (!label) {
    return null;
  }

  const item: ChecklistItem = {
    id: optionalString(value.id) ?? `item-${index}`,
    label,
    checked: value.checked === true,
  };
  const priority = isChecklistPriority(value.priority) ? value.priority : undefined;
  const assignee = optionalString(value.assignee);
  const dueDate = optionalString(value.dueDate);
  const group = optionalString(value.group);
  const notes = optionalString(value.notes);

  if (priority) item.priority = priority;
  if (assignee) item.assignee = assignee;
  if (dueDate) item.dueDate = dueDate;
  if (group) item.group = group;
  if (notes) item.notes = notes;

  return item;
}

function getChecklistGroupValue(item: ChecklistItem, groupBy: string): string {
  switch (groupBy) {
    case 'assignee':
      return item.assignee ?? 'Ungrouped';
    case 'dueDate':
      return item.dueDate ?? 'Ungrouped';
    case 'priority':
      return item.priority ?? 'Ungrouped';
    case 'group':
      return item.group ?? 'Ungrouped';
    case 'notes':
      return item.notes ?? 'Ungrouped';
    default:
      return 'Ungrouped';
  }
}

export const Checklist: React.FC<ChecklistProps> = ({ component, data: _data, isViewerMode: _isViewerMode = false }) => {
  const properties = component.properties ?? {};
  const rawItems = properties.items;
  const propItems = useMemo(
    () => Array.isArray(rawItems)
      ? rawItems
        .map((item, index) => normalizeChecklistItem(item, index))
        .filter((item): item is ChecklistItem => item !== null)
      : [],
    [rawItems],
  );
  const title = typeof properties.title === 'string' ? properties.title : '';
  const showProgress = typeof properties.showProgress === 'boolean' ? properties.showProgress : true;
  const groupBy = typeof properties.groupBy === 'string' ? properties.groupBy : undefined;

  // Local state for items — allows toggling even when component isn't in the editor store
  const [items, setItems] = useState<ChecklistItem[]>(propItems);

  // Sync local state when props change (e.g., from properties panel edits)
  useEffect(() => {
    setItems(propItems);
  }, [propItems]);

  const { checkedCount, totalCount, percentage } = useMemo(() => {
    const total = items.length;
    const checked = items.filter(item => item.checked).length;
    return { checkedCount: checked, totalCount: total, percentage: total > 0 ? Math.round((checked / total) * 100) : 0 };
  }, [items]);

  const groupedItems = useMemo(() => {
    if (!groupBy) return { '': items };
    const groups: Record<string, ChecklistItem[]> = {};
    items.forEach(item => {
      const key = getChecklistGroupValue(item, groupBy);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [items, groupBy]);

  const handleToggle = useCallback((itemId: string) => {
    setItems(prev => {
      const updatedItems = prev.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      );

      // Also try to persist to the editor store if the component exists there
      try {
        const store = useEditorStore.getState();
        const storeComp = store.components.find(c => c.id === component.id);
        if (storeComp) {
          store.updateComponent(component.id, {
            properties: { ...(storeComp.properties ?? {}), items: updatedItems },
          });
        }
      } catch {
        // Store not available — local state still works
      }

      return updatedItems;
    });
  }, [component.id]);

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Inter, system-ui, sans-serif',
    overflow: 'auto',
    padding: '12px',
    boxSizing: 'border-box',
    background: pmTheme.surface,
    color: pmTheme.text,
  };

  return (
    <div style={containerStyle} data-testid="checklist-component" role="list">
      {title && (
        <div style={{ fontSize: '16px', fontWeight: 600, color: pmTheme.text, marginBottom: '8px' }}>
          {title}
        </div>
      )}

      {showProgress && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', color: pmTheme.textMuted }}>
              {checkedCount}/{totalCount} complete ({percentage}%)
            </span>
          </div>
          <div style={{ width: '100%', height: '4px', backgroundColor: pmTheme.surfaceMuted, borderRadius: '2px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${percentage}%`,
                height: '100%',
                backgroundColor: percentage === 100 ? pmTheme.success : pmTheme.primary,
                borderRadius: '2px',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      )}

      {Object.entries(groupedItems).map(([group, groupItems]) => (
        <div key={group || 'default'}>
          {group && groupBy && (
            <details open style={{ marginBottom: '4px' }}>
              <summary style={{ fontSize: '13px', fontWeight: 600, color: pmTheme.text, cursor: 'pointer', padding: '4px 0', userSelect: 'none' }}>
                {group} ({groupItems.filter(i => i.checked).length}/{groupItems.length})
              </summary>
              {groupItems.map(item => (
                <ChecklistItemRow key={item.id} item={item} onToggle={handleToggle} />
              ))}
            </details>
          )}
          {(!group || !groupBy) && groupItems.map(item => (
            <ChecklistItemRow key={item.id} item={item} onToggle={handleToggle} />
          ))}
        </div>
      ))}
    </div>
  );
};

interface ChecklistItemRowProps {
  item: ChecklistItem;
  onToggle: (id: string) => void;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({ item, onToggle }) => {
  const priorityColor = item.priority ? PRIORITY_COLORS[item.priority] : undefined;

  return (
    <div
      role="listitem"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 8px',
        borderLeft: priorityColor ? `3px solid ${priorityColor}` : '3px solid transparent',
        borderRadius: '4px',
        marginBottom: '2px',
        backgroundColor: item.checked ? pmTheme.surfaceSubtle : 'transparent',
        transition: 'background-color 0.15s ease',
        cursor: 'pointer',
      }}
      onClick={(e) => { e.stopPropagation(); onToggle(item.id); }}
      onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onToggle(item.id); } }}
      tabIndex={0}
      aria-checked={item.checked}
    >
      <input
        type="checkbox"
        checked={item.checked}
        onChange={() => onToggle(item.id)}
        onClick={(e) => e.stopPropagation()}
        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: pmTheme.primary, flexShrink: 0 }}
        tabIndex={-1}
        aria-label={`${item.label} ${item.checked ? 'completed' : 'pending'}`}
      />
      <span
        style={{
          flex: 1,
          fontSize: '14px',
          color: item.checked ? pmTheme.textSubtle : pmTheme.text,
          textDecoration: item.checked ? 'line-through' : 'none',
          opacity: item.checked ? 0.6 : 1,
          transition: 'opacity 0.15s ease',
        }}
      >
        {item.label}
      </span>
      {item.assignee && (
        <span style={{
          fontSize: '11px',
          color: pmTheme.textMuted,
          backgroundColor: pmTheme.surfaceMuted,
          padding: '1px 6px',
          borderRadius: '10px',
          whiteSpace: 'nowrap',
        }}>
          {item.assignee}
        </span>
      )}
      {item.dueDate && (
        <span style={{ fontSize: '11px', color: pmTheme.textSubtle, whiteSpace: 'nowrap' }}>
          {new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      )}
    </div>
  );
};

Checklist.displayName = 'Checklist';
